const SELECTORS = {
  button: "[data-psp-variant-id]",
};

const ACTIVE_CLASSES = ["is-active", "bg-(--paper-active-bg)", "text-(--paper-active-text)"];
const INACTIVE_CLASSES = ["bg-white", "text-gray-700", "hover:bg-gray-50"];

class PaperSizePicker extends HTMLElement {
  #buttons = [];
  #activeButton = null;

  connectedCallback() {
    try {
      this.#init();
    } catch (error) {
      console.error("[PaperSizePicker:init]", error);
    }
  }

  #init() {
    this.#buttons = Array.from(this.querySelectorAll(SELECTORS.button));
    this.#activeButton =
      this.#buttons.find((button) => button.classList.contains("is-active")) ??
      this.#buttons[0] ??
      null;
    this.#buttons.forEach((button) => {
      button.addEventListener("click", this.#onSelect.bind(this, button));
    });
    // The sibling-mode format selector suppresses its variant:request reply when
    // this picker is present (data-has-size-picker) — the picker owns the variant
    // state, so late-joining listeners get the current size selection from here.
    document.addEventListener("variant:request", () => {
      this.#dispatchVariantChange();
    });
  }

  #onSelect(button) {
    if (button === this.#activeButton || button.disabled) return;

    const variantId = Number.parseInt(button.dataset.pspVariantId, 10);
    if (!Number.isInteger(variantId) || variantId <= 0) {
      console.error("[PaperSizePicker] Invalid variant ID:", button.dataset.pspVariantId);
      return;
    }

    this.#buttons.forEach((candidate) => {
      const isActive = candidate === button;
      candidate.classList.toggle("is-active", isActive);
      candidate.setAttribute("aria-pressed", isActive ? "true" : "false");
      if (isActive) {
        candidate.classList.add(...ACTIVE_CLASSES);
        candidate.classList.remove(...INACTIVE_CLASSES);
      } else {
        candidate.classList.remove(...ACTIVE_CLASSES);
        candidate.classList.add(...INACTIVE_CLASSES);
      }
    });
    this.#activeButton = button;

    const url = new URL(window.location);
    url.searchParams.set("variant", variantId);
    window.history.replaceState({}, "", url);

    this.#dispatchVariantChange();
  }

  #dispatchVariantChange() {
    if (!this.#activeButton) return;
    const variantId = Number.parseInt(this.#activeButton.dataset.pspVariantId, 10);
    if (!Number.isInteger(variantId) || variantId <= 0) return;

    document.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: {
          variantId,
          price: this.#parseJsonDataset("pspPrice"),
          comparePrice: this.#parseJsonDataset("pspComparePrice"),
          isDigital: false,
        },
      }),
    );
  }

  #parseJsonDataset(datasetKey) {
    const rawValue = this.#activeButton?.dataset[datasetKey];
    if (!rawValue) return null;
    try {
      return JSON.parse(rawValue);
    } catch {
      console.error(`[PaperSizePicker] Invalid JSON in data-${datasetKey}:`, rawValue);
      return null;
    }
  }
}

if (!customElements.get("paper-size-picker")) {
  customElements.define("paper-size-picker", PaperSizePicker);
}
